import React from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { FormValuesModel } from '.';
import { useFormikContext } from 'formik';
import { INTAKE_FORM_FIELDS_GRID_SIZES } from 'constants/grids';
import { getPlaceholderForInputField, getPlaceholderForSelect } from '../utils';

// components
import FieldIssue from '../FieldIssue';
import SubmitButtons from '../SubmitButtons';
import HideIfDisabledForm from 'components/Forms/HideIfDisabledForm';
import TotalTransactionsPerMonthSelect from 'components/Forms/FormComponents/SelectInputs/CRM/TotalTransactionsPerMonthSelect';
import PurposeOfBankAccountSelect, {
  IProps as PurposeOfBankAccountSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/PurposeOfBankAccountSelect';
import ProjectedRevenueSelect, {
  IProps as ProjectedRevenueSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/ProjectedRevenueSelect';
import AverageNumberOfTransactionsSelect, {
  IProps as AverageNumberOfTransactionsSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/AverageNumberOfTransactionsSelect';
import CountriesAutocomplete, {
  IProps as CountriesAutocompletePropsModel,
} from 'components/Forms/FormComponents/Autocompletes/CRM/CountriesAutocomplete';
import {
  Col,
  Row,
  Divider,
  FormField,
  FormInputTextArea,
  FormInputTextAreaProps,
} from '@ui';

const InnerForm = () => {
  const { t } = useTranslation('onboarding');
  const { values } = useFormikContext<FormValuesModel>();

  return (
    <>
      <Row wrap={true} justify="start">
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<PurposeOfBankAccountSelectProps>
            label={
              <FieldIssue fieldId="purposeOfAccount">
                {t('intake_form.account.purpose')}
              </FieldIssue>
            }
            name="purpose"
            component={PurposeOfBankAccountSelect}
            additionalProps={{
              mode: 'multiple',
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, true, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<CountriesAutocompletePropsModel>
            label={
              <FieldIssue fieldId="accountCountries">
                {t('intake_form.account.transaction_countries')}
              </FieldIssue>
            }
            name="transactionCountries"
            component={CountriesAutocomplete}
            additionalProps={{
              mode: 'multiple',
              initialValue: values.transactionCountriesInitialOptions,
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, true, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<AverageNumberOfTransactionsSelectProps>
            label={
              <FieldIssue fieldId="incomingTransactionNumber">
                {t('intake_form.account.number_of_incoming_transactions')}
              </FieldIssue>
            }
            name="numberOfIncomingTransactions"
            component={AverageNumberOfTransactionsSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<ProjectedRevenueSelectProps>
            label={
              <FieldIssue fieldId="incomingTransactionVolume">
                {t('intake_form.account.value_of_incoming_transactions')}
              </FieldIssue>
            }
            name="valueOfIncomingTransactions"
            component={TotalTransactionsPerMonthSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<AverageNumberOfTransactionsSelectProps>
            label={
              <FieldIssue fieldId="outgoingTransactionNumber">
                {t('intake_form.account.number_of_outgoing_transactions')}
              </FieldIssue>
            }
            name="numberOfOutgoingTransactions"
            component={AverageNumberOfTransactionsSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<ProjectedRevenueSelectProps>
            label={
              <FieldIssue fieldId="outgoingTransactionVolume">
                {t('intake_form.account.value_of_outgoing_transactions')}
              </FieldIssue>
            }
            name="valueOfOutgoingTransactions"
            component={TotalTransactionsPerMonthSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<ProjectedRevenueSelectProps>
            label={
              <FieldIssue fieldId="avarageMonthlyBalance">
                {t('intake_form.account.average_monthly_balance')}
              </FieldIssue>
            }
            name="monthlyBalanceAmount"
            component={ProjectedRevenueSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormInputTextAreaProps>
            label={
              <FieldIssue fieldId="accountInflowsDetails">
                {t('intake_form.account.account_inflows_details')}
              </FieldIssue>
            }
            name="accountInflowsDetails"
            component={FormInputTextArea}
            additionalProps={{
              rows: 5,
              renderPlaceholder: (validationRules) =>
                getPlaceholderForInputField(validationRules, true, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormInputTextAreaProps>
            label={
              <FieldIssue fieldId="accountOutflowsDetails">
                {t('intake_form.account.account_outflows_details')}
              </FieldIssue>
            }
            name="accountOutflowsDetails"
            component={FormInputTextArea}
            additionalProps={{
              rows: 5,
              renderPlaceholder: (validationRules) =>
                getPlaceholderForInputField(validationRules, true, t),
            }}
          />
        </Col>
      </Row>
      <HideIfDisabledForm>
        <Divider />
        <SubmitButtons />
      </HideIfDisabledForm>
    </>
  );
};

export default InnerForm;
